import React from "react";
import { createPortal } from "react-dom";

import {
  completeAvatarUpload,
  createAvatarUploadUrl,
  uploadAvatarToCloudflare,
} from "../../../helpers/api/user";

export const MAX_AVATAR_FILE_SIZE = 3 * 1024 * 1024;
const CROP_SIZE = 320;
const OUTPUT_SIZE = 512;

export const validateAvatarFile = (file: File) => {
  if (!file.type.startsWith("image/") || file.type === "image/svg+xml") {
    return "Choose a safe raster image such as JPEG, PNG, WebP, GIF or AVIF.";
  }
  if (file.size > MAX_AVATAR_FILE_SIZE) return "The image must be 3 MB or smaller.";
  return null;
};

export const constrainAvatarOffset = (
  value: { x: number; y: number },
  image: Pick<HTMLImageElement, "naturalWidth" | "naturalHeight">,
  zoom: number,
  cropSize = CROP_SIZE,
) => {
  const baseScale = Math.max(cropSize / image.naturalWidth, cropSize / image.naturalHeight);
  const width = image.naturalWidth * baseScale * zoom;
  const height = image.naturalHeight * baseScale * zoom;
  const maxX = Math.max(0, (width - cropSize) / 2);
  const maxY = Math.max(0, (height - cropSize) / 2);
  return {
    x: maxX === 0 ? 0 : Math.max(-maxX, Math.min(maxX, value.x)),
    y: maxY === 0 ? 0 : Math.max(-maxY, Math.min(maxY, value.y)),
  };
};

export const createAvatarBlob = (
  image: HTMLImageElement,
  zoom: number,
  offset: { x: number; y: number },
  cropSize = CROP_SIZE,
): Promise<Blob> => {
  const canvas = document.createElement("canvas");
  canvas.width = OUTPUT_SIZE;
  canvas.height = OUTPUT_SIZE;
  const context = canvas.getContext("2d");
  if (!context) return Promise.reject(new Error("Image editing is not supported by this browser."));

  const baseScale = Math.max(cropSize / image.naturalWidth, cropSize / image.naturalHeight);
  const scale = baseScale * zoom;
  const sourceSize = cropSize / scale;
  const sourceX = (image.naturalWidth - sourceSize) / 2 - offset.x / scale;
  const sourceY = (image.naturalHeight - sourceSize) / 2 - offset.y / scale;
  context.drawImage(image, sourceX, sourceY, sourceSize, sourceSize, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => blob ? resolve(blob) : reject(new Error("Unable to prepare the avatar image.")),
      "image/jpeg",
      0.9,
    );
  });
};

interface AvatarEditorModalProps {
  onClose: () => void;
  onSaved: (avatar: string) => void;
}

const AvatarEditorModal = ({ onClose, onSaved }: AvatarEditorModalProps) => {
  const dialogRef = React.useRef<HTMLElement>(null);
  const cropRef = React.useRef<HTMLDivElement>(null);
  const closeButtonRef = React.useRef<HTMLButtonElement>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const dragRef = React.useRef<{ pointerId: number; x: number; y: number; originX: number; originY: number } | null>(null);
  const previewUrlRef = React.useRef<string | null>(null);
  const isSavingRef = React.useRef(false);
  const onCloseRef = React.useRef(onClose);
  const [image, setImage] = React.useState<HTMLImageElement | null>(null);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [zoom, setZoom] = React.useState(1);
  const [offset, setOffset] = React.useState({ x: 0, y: 0 });
  const [error, setError] = React.useState("");
  const [isDraggingFile, setIsDraggingFile] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [cropSize, setCropSize] = React.useState(CROP_SIZE);

  React.useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  React.useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !isSavingRef.current) onCloseRef.current();
      if (event.key !== "Tab") return;
      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>("button:not(:disabled), input:not([type=file]):not(:disabled)");
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previewUrlRef.current) URL.revokeObjectURL?.(previewUrlRef.current);
    };
  }, []);

  React.useEffect(() => {
    isSavingRef.current = isSaving;
  }, [isSaving]);

  React.useLayoutEffect(() => {
    if (!image) return undefined;
    const measureCrop = () => setCropSize(cropRef.current?.clientWidth || CROP_SIZE);
    measureCrop();
    window.addEventListener("resize", measureCrop);
    return () => window.removeEventListener("resize", measureCrop);
  }, [image]);

  const selectFile = (file?: File) => {
    if (!file) return;
    const validationError = validateAvatarFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    const url = URL.createObjectURL(file);
    const nextImage = new Image();
    nextImage.onload = () => {
      if (previewUrlRef.current) URL.revokeObjectURL?.(previewUrlRef.current);
      previewUrlRef.current = url;
      setPreviewUrl(url);
      setImage(nextImage);
      setZoom(1);
      setOffset({ x: 0, y: 0 });
      setError("");
    };
    nextImage.onerror = () => {
      URL.revokeObjectURL?.(url);
      setError("This image cannot be displayed safely by your browser.");
    };
    nextImage.src = url;
  };

  const updateZoom = (nextZoom: number) => {
    setZoom(nextZoom);
    if (image) setOffset((current) => constrainAvatarOffset(current, image, nextZoom, cropSize));
  };

  const saveAvatar = async () => {
    if (!image) return;
    setIsSaving(true);
    setError("");
    try {
      const blob = await createAvatarBlob(image, zoom, offset, cropSize);
      const { imageId, uploadURL } = await createAvatarUploadUrl();
      await uploadAvatarToCloudflare(blob, uploadURL);
      const response = await completeAvatarUpload(imageId);
      onSaved(response.avatar);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to save the avatar.");
    } finally {
      setIsSaving(false);
    }
  };

  const displayScale = image ? Math.max(cropSize / image.naturalWidth, cropSize / image.naturalHeight) * zoom : 1;

  return createPortal(
    <div
      className="avatar-editor__backdrop"
      role="presentation"
      data-cy="avatar-editor-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isSaving) onClose();
      }}
    >
      <section
        ref={dialogRef}
        className="avatar-editor"
        role="dialog"
        aria-modal="true"
        aria-busy={isSaving}
        aria-labelledby="avatar-editor-title"
        data-cy="avatar-editor-modal"
      >
        <header className="avatar-editor__header">
          <div>
            <p>Profile image</p>
            <h2 id="avatar-editor-title">Edit your avatar</h2>
          </div>
          <button ref={closeButtonRef} type="button" aria-label="Close avatar editor" onClick={onClose} disabled={isSaving}>
            <i className="ri-close-line" aria-hidden="true" />
          </button>
        </header>

        {!image ? (
          <div
            className={`avatar-editor__dropzone${isDraggingFile ? " is-dragging" : ""}`}
            onDragEnter={(event) => { event.preventDefault(); setIsDraggingFile(true); }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node)) setIsDraggingFile(false); }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDraggingFile(false);
              selectFile(event.dataTransfer.files[0]);
            }}
            data-cy="avatar-dropzone"
          >
            <i className="ri-image-add-line" aria-hidden="true" />
            <strong>Drop your image here</strong>
            <span>or choose a file from your device</span>
            <button type="button" onClick={() => fileInputRef.current?.click()} data-cy="avatar-choose-file">Choose image</button>
            <small>Raster images only, up to 3 MB</small>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(event) => {
                selectFile(event.target.files?.[0]);
                event.target.value = "";
              }}
              data-cy="avatar-file-input"
            />
          </div>
        ) : (
          <div className="avatar-editor__workspace">
            <div
              ref={cropRef}
              className="avatar-editor__crop"
              onPointerDown={(event) => {
                event.currentTarget.setPointerCapture(event.pointerId);
                dragRef.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, originX: offset.x, originY: offset.y };
              }}
              onPointerMove={(event) => {
                const drag = dragRef.current;
                if (!drag || drag.pointerId !== event.pointerId || !image) return;
                setOffset(constrainAvatarOffset({
                  x: drag.originX + event.clientX - drag.x,
                  y: drag.originY + event.clientY - drag.y,
                }, image, zoom, cropSize));
              }}
              onPointerUp={() => { dragRef.current = null; }}
              data-cy="avatar-crop-area"
            >
              <img
                src={previewUrl || ""}
                alt="Avatar crop preview"
                draggable={false}
                style={{
                  width: `${image.naturalWidth * displayScale}px`,
                  height: `${image.naturalHeight * displayScale}px`,
                  transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))`,
                }}
              />
              <div className="avatar-editor__crop-mask" aria-hidden="true" />
            </div>
            <label className="avatar-editor__zoom">
              <span><i className="ri-zoom-in-line" aria-hidden="true" /> Zoom</span>
              <input
                type="range"
                min="1"
                max="3"
                step="0.01"
                value={zoom}
                onChange={(event) => updateZoom(Number(event.target.value))}
                data-cy="avatar-zoom"
              />
            </label>
            <button type="button" className="avatar-editor__replace" onClick={() => fileInputRef.current?.click()} disabled={isSaving}>
              Choose another image
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(event) => {
                selectFile(event.target.files?.[0]);
                event.target.value = "";
              }}
            />
          </div>
        )}

        {error && <p className="avatar-editor__error" role="alert">{error}</p>}

        <footer className="avatar-editor__actions">
          <button type="button" onClick={onClose} disabled={isSaving}>Cancel</button>
          <button type="button" onClick={saveAvatar} disabled={!image || isSaving} data-cy="avatar-save">
            {isSaving ? "Saving..." : "Save avatar"}
          </button>
        </footer>

        {isSaving && (
          <div className="avatar-editor__saving" role="status" aria-live="polite" data-cy="avatar-saving-loader">
            <span className="avatar-editor__spinner" aria-hidden="true" />
            <span>Saving your avatar...</span>
          </div>
        )}
      </section>
    </div>,
    document.body,
  );
};

export default AvatarEditorModal;
